/**
 * EPIC DXC strings. Arabic is the source language.
 *
 * The vocabulary matters here more than usual. A "حالة تشخيصية" is not a visit
 * and not an appointment, and reception has to be able to tell the three apart
 * at a glance - so nothing in this file borrows a word from either.
 */
export const diagnosticCasesAr: Record<string, string> = {
  'nav.diagnosticCases': 'الحالات التشخيصية',

  'dxc.title': 'الحالات التشخيصية',
  'dxc.subtitle': 'مرضى يراجعون للفحوص فقط — بإحالة خارجية أو مراجعة مباشرة، دون زيارة طبيب',
  'dxc.new': 'حالة تشخيصية جديدة',
  'dxc.empty': 'لا توجد حالات تشخيصية',
  'dxc.emptyBody': 'ابدأ بحالة جديدة عندما يصل مريض لإجراء فحوص دون موعد مع طبيب.',
  'dxc.searchPlaceholder': 'رقم الحالة، اسم المريض أو رقم الملف…',

  // --- Status --------------------------------------------------------------
  'dxc.status.NEW': 'جديدة',
  'dxc.status.IN_PROGRESS': 'قيد التنفيذ',
  'dxc.status.RESULTS_READY': 'النتائج جاهزة',
  'dxc.status.COMPLETED': 'مُسلَّمة',
  'dxc.status.CANCELLED': 'ملغاة',

  'dxc.source.WALK_IN': 'مراجعة مباشرة',
  'dxc.source.EXTERNAL_REFERRAL': 'إحالة خارجية',

  // --- Columns and labels --------------------------------------------------
  'dxc.column.caseNumber': 'رقم الحالة',
  'dxc.column.patient': 'المريض',
  'dxc.column.source': 'المصدر',
  'dxc.column.orders': 'الطلبات',
  'dxc.column.status': 'الحالة',
  'dxc.column.opened': 'تاريخ الفتح',

  'dxc.label.referringDoctor': 'الطبيب المُحيل',
  'dxc.label.facility': 'الجهة المُحيلة',
  'dxc.label.referralNumber': 'رقم الإحالة',
  'dxc.label.indication': 'دواعي الفحص',
  'dxc.label.notes': 'ملاحظات إدارية',
  'dxc.label.lab': 'المختبر',
  'dxc.label.imaging': 'الأشعة',
  'dxc.label.mrn': 'رقم الملف',
  'dxc.label.branch': 'الفرع',

  // --- The wizard ----------------------------------------------------------
  'dxc.wizard.title': 'حالة تشخيصية جديدة',
  'dxc.wizard.step.patient': 'المريض',
  'dxc.wizard.step.source': 'المصدر والإحالة',
  'dxc.wizard.step.services': 'الفحوص',
  'dxc.wizard.step.confirm': 'المراجعة والتأكيد',
  'dxc.wizard.searchPatient': 'ابحث عن المريض',
  'dxc.wizard.searchPatientHint': 'بالاسم أو رقم الملف أو رقم الهاتف',
  'dxc.wizard.registerPatient': 'تسجيل مريض جديد',
  'dxc.wizard.selectedPatient': 'المريض المختار',
  'dxc.wizard.change': 'تغيير',
  'dxc.wizard.searchProvider': 'ابحث عن الطبيب المُحيل',
  'dxc.wizard.newProvider': 'إضافة طبيب مُحيل',
  'dxc.wizard.providerName': 'اسم الطبيب',
  'dxc.wizard.providerSpecialty': 'التخصص',
  'dxc.wizard.providerFacility': 'الجهة / المستشفى',
  'dxc.wizard.providerPhone': 'الهاتف',
  'dxc.wizard.saveProvider': 'حفظ الطبيب',
  'dxc.wizard.noProvider': 'بدون طبيب مُحيل',
  'dxc.wizard.labTests': 'فحوص المختبر',
  'dxc.wizard.searchTests': 'ابحث في دليل الفحوص',
  'dxc.wizard.selectedTests': 'المختار: {count}',
  'dxc.wizard.imagingStudies': 'فحوص الأشعة',
  'dxc.wizard.addStudy': 'إضافة فحص أشعة',
  'dxc.wizard.modality': 'نوع الفحص',
  'dxc.wizard.bodyRegion': 'المنطقة',
  'dxc.wizard.remove': 'إزالة',
  'dxc.wizard.back': 'السابق',
  'dxc.wizard.next': 'التالي',
  'dxc.wizard.submit': 'إنشاء الحالة',
  'dxc.wizard.needsWork': 'اختر فحصاً واحداً على الأقل من المختبر أو الأشعة.',
  'dxc.wizard.needsPatient': 'اختر المريض أولاً.',
  'dxc.wizard.created': 'أُنشئت الحالة {number}',
  'dxc.wizard.createdBody': 'ظهرت الطلبات في قوائم عمل المختبر والأشعة.',
  'dxc.wizard.openCase': 'فتح الحالة',
  'dxc.wizard.panel': 'باقة',

  // --- Detail --------------------------------------------------------------
  'dxc.detail.orders': 'الطلبات',
  'dxc.detail.noOrders': 'لا توجد طلبات',
  'dxc.detail.finance': 'المالية',
  'dxc.detail.noInvoice': 'لم تُصدر فاتورة بعد',
  'dxc.detail.invoiceTotal': 'الإجمالي',
  'dxc.detail.invoicePaid': 'المدفوع',
  'dxc.detail.invoiceBalance': 'المتبقي',
  'dxc.detail.checkout': 'إصدار فاتورة وتحصيل',
  'dxc.detail.print': 'طباعة تقرير الحالة',
  'dxc.detail.complete': 'تسليم النتائج',
  'dxc.detail.cancel': 'إلغاء الحالة',
  'dxc.detail.cancelReason': 'سبب الإلغاء',
  'dxc.detail.confirmCancel': 'تأكيد الإلغاء',
  'dxc.detail.completeHint': 'يتاح التسليم بعد اعتماد كل النتائج',
  'dxc.detail.pending': 'بانتظار الاعتماد: {orders}',
  'dxc.detail.result': 'النتيجة',
  'dxc.detail.reference': 'النطاق المرجعي',
  'dxc.detail.awaitingResult': 'بانتظار النتيجة',
  'dxc.detail.abnormal': 'خارج النطاق',
  'dxc.detail.critical': 'حرجة',
  'dxc.detail.reportFinal': 'التقرير معتمد',
  'dxc.detail.reportAmended': 'التقرير مُعدَّل',
  'dxc.detail.reportPending': 'التقرير قيد الإعداد',

  // --- Checkout ------------------------------------------------------------
  'dxc.checkout.title': 'تحصيل الحالة التشخيصية',
  'dxc.checkout.nothingToBill': 'كل الطلبات مفوترة بالفعل',
  'dxc.checkout.total': 'الإجمالي',
  'dxc.checkout.method': 'طريقة الدفع',
  'dxc.checkout.amount': 'المبلغ المستلم',
  'dxc.checkout.collect': 'تحصيل',
  'dxc.checkout.issueUnpaid': 'إصدار فاتورة برصيد مستحق',
  'dxc.checkout.done': 'تم إصدار الفاتورة {number}',
  'dxc.checkout.receipt': 'سند القبض {number}',
  'dxc.checkout.unpricedHint':
    'فحص أشعة بدون خدمة مسعّرة لا يمكن فوترته. أضف خدمة له من «الخدمات والأسعار».',

  // --- Origin, shown on the lab and imaging worklists ----------------------
  'dxc.origin.visit': 'زيارة',
  'dxc.origin.case': 'حالة تشخيصية',
  'dxc.origin.none': 'طلب مباشر',
  'dxc.origin.caseTooltip': 'طلب من حالة تشخيصية — لا توجد زيارة طبيب',
};
